import { createFileRoute, type ToOptions } from '@tanstack/react-router'
import { Calendar, CalendarCheck2, Users, type LucideIcon } from 'lucide-react'
import z from 'zod/v3'

import { typedObjectEntries } from '@gozoki/tools'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { periods } from '~/components/new/app-ui/period-select'
import { StoreCombobox } from '~/components/new/app-ui/store-combobox'
import { Title } from '~/components/new/app-ui/title'
import { PlanningAttendance } from '~/components/routes/hr/planning/attendance'
import { PlanningCollaborator } from '~/components/routes/hr/planning/collaborators'
import { PlanningCalendar } from '~/components/routes/hr/planning/planning'
import { PlanningSettingsDialog } from '~/components/routes/hr/planning/planning/planning-settings'

const tabValues = ['calendar', 'collaborators', 'attendance'] as const
type TabValue = (typeof tabValues)[number]

export const Route = createFileRoute('/_authenticated/hr/planning/$tab')({
  component: RouteComponent,
  params: {
    parse: z.object({ tab: z.enum(tabValues) }).parse,
  },
  validateSearch: z.object({
    // Global search params
    storeId: z.number().optional(),

    // Dashboard specific search params
    period: z.enum(periods).optional(),
  }),
})

const tabOptions = {
  // dashboard: {
  //   label: 'Tableau de bord',
  //   icon: LayoutGrid,
  //   toOptions: { to: '/hr/planning/$tab', params: { tab: 'dashboard' } },
  //   component: <PlanningDashboard />,
  // },
  calendar: {
    label: 'Planning',
    icon: Calendar,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'calendar' } },
    component: <PlanningCalendar />,
  },
  collaborators: {
    label: 'Personnel',
    icon: Users,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'collaborators' } },
    component: <PlanningCollaborator />,
  },
  attendance: {
    label: 'Pointage',
    icon: CalendarCheck2,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'attendance' } },
    component: <PlanningAttendance />,
  },
} as const satisfies Record<
  TabValue,
  { toOptions: ToOptions; component: React.ReactNode; label: string; icon: LucideIcon }
>

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { storeId } = Route.useSearch()
  const { tab } = Route.useParams()

  const TabComponent = tabOptions[tab].component

  return (
    <>
      <AppHeader>
        <Title>RH & Planning</Title>
      </AppHeader>

      <AppContainer className="space-y-4">
        <div className="flex items-center justify-between">
          <AppTabs
            tabs={typedObjectEntries(tabOptions).map(([key, value]) => ({
              label: value.label,
              icon: value.icon,
              value: key,
            }))}
            value={tab}
            onTabChange={(v) =>
              navigate({
                ...tabOptions[v].toOptions,
                search: (prev) => ({ storeId: prev.storeId }),
              })
            }
          />
          <div className="flex items-center gap-2">
            {tab === 'calendar' && <PlanningSettingsDialog />}
            <StoreCombobox
              onChange={(v) =>
                navigate({ search: (prev) => ({ ...prev, storeId: v ?? undefined }) })
              }
              value={storeId ?? null}
            />
          </div>
        </div>

        {TabComponent}
      </AppContainer>
    </>
  )
}
